import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import type { FormData } from "../FormRenderer/types";
import { useAsyncOptions } from "../FormRenderer/useAsyncOptions";
import type { PropertyValueItem, WorkflowTaskDetail } from "../../services/regulationsService";
import { resolveTaskOwnersGroupedInitial } from "../../utils/taskDetailToDynamicFormConfig";
import {
  buildAvailableOwnersSummary,
  findTaskTableHeader,
  formatHeaderSummaryDisplayValue,
  hasNonEmptyHeaderFieldValue,
  isAvailableOwnersHeader,
  parseTaskTableHeadersFromTaskDetail,
  resolveTaskHeaderLabel,
} from "../../utils/taskTableHeaders";
import { normalizeTaskTableAsyncConfig } from "../../utils/taskTableAsyncConfig";
import type { TaskTableField } from "../../types/manualAssessmentWorkflow";
import type { TaskFormSummary } from "./ManualAssessmentForm";

const TASK_SUMMARY_LABEL_SX = {
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 400,
  fontSize: 14,
  lineHeight: "20px",
  letterSpacing: 0,
  color: "#8C8C8C",
} as const;

const TASK_SUMMARY_VALUE_SX = {
  fontFamily: '"Nunito Sans", sans-serif',
  fontWeight: 400,
  fontSize: 14,
  lineHeight: "20px",
  letterSpacing: 0,
  verticalAlign: "middle",
  color: "#262626",
} as const;

const PLACEHOLDER = "—";

function headerAsAsyncField(header: PropertyValueItem | undefined): TaskTableField | undefined {
  if (!header) return undefined;
  return header as unknown as TaskTableField;
}

function SummaryField({
  label,
  value,
  tooltip,
}: Readonly<{
  label: string;
  value: React.ReactNode;
  tooltip?: string | null;
}>) {
  const hasTooltip = Boolean(tooltip?.trim());
  const content = (
    <Typography component="span" sx={TASK_SUMMARY_LABEL_SX}>
      {label}:{" "}
      <Box component="span" sx={TASK_SUMMARY_VALUE_SX}>
        {value ?? PLACEHOLDER}
      </Box>
    </Typography>
  );

  if (!hasTooltip) return content;

  return (
    <Tooltip
      title={tooltip ?? ""}
      arrow
      slotProps={{
        tooltip: {
          sx: { maxWidth: 560, whiteSpace: "normal" },
        },
      }}
    >
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "baseline",
          flexWrap: "wrap",
          ...TASK_SUMMARY_LABEL_SX,
          cursor: "help",
        }}
      >
        {label}:{" "}
        <Box component="span" sx={TASK_SUMMARY_VALUE_SX}>
          {value ?? PLACEHOLDER}
        </Box>
      </Box>
    </Tooltip>
  );
}

/**
 * Header strip driven by the same PropertyValueItem schema as DynamicForm.
 * Empty fieldValue → skip. Available Owners is the only specialty (person count
 * from task-assignees options). All other fields format by type / options.
 * No frontend-only fields (e.g. Assessor) are appended — only `headers` items.
 */
function HeaderDrivenSummary({
  headers,
}: Readonly<{
  headers: PropertyValueItem[];
}>) {
  const taskOwnersHeader = findTaskTableHeader(headers, "taskOwners");
  const taskAssigneeHeader = findTaskTableHeader(headers, "taskAssignee");

  const [resolvedTaskOwners, setResolvedTaskOwners] = useState<unknown>(
    taskOwnersHeader?.fieldValue,
  );

  useEffect(() => {
    let cancelled = false;
    const fieldValue = taskOwnersHeader?.fieldValue;
    setResolvedTaskOwners(fieldValue);
    if (fieldValue == null) return undefined;

    void Promise.resolve(resolveTaskOwnersGroupedInitial(fieldValue)).then(
      (corrected) => {
        if (cancelled) return;
        setResolvedTaskOwners(corrected ?? fieldValue);
      },
    );

    return () => {
      cancelled = true;
    };
  }, [taskOwnersHeader?.fieldValue]);

  const asyncFormData = useMemo<FormData>(
    () =>
      resolvedTaskOwners !== undefined
        ? ({ taskOwners: resolvedTaskOwners } as FormData)
        : ({} as FormData),
    [resolvedTaskOwners],
  );

  const assigneeAsyncConfig = normalizeTaskTableAsyncConfig(
    headerAsAsyncField(taskAssigneeHeader),
  );
  const { options: assigneeOptions } = useAsyncOptions(
    assigneeAsyncConfig,
    asyncFormData,
  );

  const availableOwners = useMemo(
    () => buildAvailableOwnersSummary(assigneeOptions),
    [assigneeOptions],
  );

  const ownerCountText =
    availableOwners.count > 0
      ? `${availableOwners.count} ${
          availableOwners.count === 1 ? "person" : "people"
        }`
      : null;

  return (
    <>
      {headers.map((header, index) => {
        const field = String(header.field ?? "");
        const key = `${field || "header"}-${index}`;

        if (isAvailableOwnersHeader(header)) {
          if (!hasNonEmptyHeaderFieldValue(header) || !ownerCountText) {
            return null;
          }
          return (
            <SummaryField
              key={key}
              label={resolveTaskHeaderLabel(header, "Available Owners")}
              value={ownerCountText}
              tooltip={availableOwners.names}
            />
          );
        }

        if (!hasNonEmptyHeaderFieldValue(header)) return null;

        const options =
          field === "taskAssignee" && assigneeOptions.length > 0
            ? assigneeOptions
            : undefined;
        const display = formatHeaderSummaryDisplayValue(header, options);
        if (!display) return null;

        return (
          <SummaryField
            key={key}
            label={resolveTaskHeaderLabel(header, field || "Field")}
            value={display}
          />
        );
      })}
    </>
  );
}

function LegacySummary({
  summary,
  extraFields,
}: Readonly<{
  summary: TaskFormSummary;
  extraFields: React.ReactNode;
}>) {
  const ownerCountText =
    summary.availableOwnerCount > 0
      ? `${summary.availableOwnerCount} ${
          summary.availableOwnerCount === 1 ? "person" : "people"
        }`
      : null;

  return (
    <>
      {ownerCountText ? (
        <SummaryField
          label="Available Owners"
          value={ownerCountText}
          tooltip={summary.availableOwnerNames}
        />
      ) : null}
      {summary.currentAssigneeName ? (
        <SummaryField
          label="Current Assignee"
          value={summary.currentAssigneeName}
        />
      ) : null}
      {summary.dueDate ? (
        <SummaryField label="Due date" value={summary.dueDate} />
      ) : null}
      {extraFields}
    </>
  );
}

export type TaskFormSummaryBarProps = Readonly<{
  taskDetail: WorkflowTaskDetail | null;
  summary: TaskFormSummary | null;
  title?: string;
}>;

export function TaskFormSummaryBar({
  taskDetail,
  summary,
  title,
}: TaskFormSummaryBarProps) {
  if (!summary || !taskDetail) return null;

  const headers = parseTaskTableHeadersFromTaskDetail(taskDetail);
  const useHeaders = headers.length > 0;

  // When taskPage/task headers exist, the strip is schema-driven only.
  // Assessor / Date Of Impact are legacy fallbacks for payloads without headers.
  const legacyExtraFields = (
    <>
      {summary.showDateOfImpactAssessment ? (
        <SummaryField
          label="Date Of Impact Assessment"
          value={summary.dateOfImpactAssessment}
        />
      ) : null}
      {summary.assessorName &&
      (title ?? "").toLowerCase().includes("approve") ? (
        <SummaryField label="Assessor" value={summary.assessorName} />
      ) : null}
    </>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        columnGap: 3,
        rowGap: 1,
        mb: 2,
      }}
    >
      {useHeaders ? (
        <HeaderDrivenSummary headers={headers} />
      ) : (
        <LegacySummary summary={summary} extraFields={legacyExtraFields} />
      )}
    </Box>
  );
}

export default TaskFormSummaryBar;
